import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Warehouse, Plus, Pencil, Trash2, MapPin, LocateFixed, ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import * as whApi from '@/services/warehousesApi';

const emptyForm = { name: '', code: '', latitude: '', longitude: '', active: true };

// ---- Admin tool: bulk re-tag legacy "Unmapped" inward entries ----
const UnmappedRetagTool = ({ warehouses }) => {
  const [count, setCount] = useState(null);
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const limit = 50;
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [targetWh, setTargetWh] = useState('');
  const [assigning, setAssigning] = useState(false);

  const activeWarehouses = (warehouses || []).filter((w) => w.active !== false);

  const loadCount = async () => {
    try { const d = await whApi.getUnmappedCount(); setCount(d.count); } catch { /* non-blocking */ }
  };

  const loadEntries = async (p = 0) => {
    setLoading(true);
    try {
      const params = { skip: p * limit, limit };
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (search.trim()) params.search = search.trim();
      const d = await whApi.getUnmappedEntries(params);
      setEntries(d.entries || []);
      setTotal(d.total || 0);
      setPage(p);
      setSelected(new Set());
    } catch {
      toast.error('Failed to load unmapped entries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCount(); loadEntries(0); /* eslint-disable-next-line */ }, []);

  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === entries.length) setSelected(new Set());
    else setSelected(new Set(entries.map((e) => e.entry_id)));
  };

  const assign = async () => {
    if (!targetWh) { toast.error('Pick a warehouse to assign'); return; }
    if (selected.size === 0) { toast.error('Select at least one entry'); return; }
    setAssigning(true);
    try {
      const res = await whApi.bulkRetagEntries([...selected], targetWh);
      toast.success(`${res.updated} entr${res.updated === 1 ? 'y' : 'ies'} tagged to ${res.warehouse_name}`);
      await loadCount();
      await loadEntries(page);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to re-tag');
    } finally {
      setAssigning(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const fmtDate = (ts) => {
    if (!ts) return '—';
    try { return new Date(ts).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); }
    catch { return String(ts).slice(0, 16); }
  };

  return (
    <div className="mt-8 bg-white rounded-lg border border-[#E5DFD0] overflow-hidden" data-testid="retag-tool">
      <div className="bg-[#F5F3EC] px-4 py-3 flex items-center gap-3 flex-wrap border-b border-[#E5DFD0]">
        <MapPin className="w-5 h-5 text-amber-600" />
        <div className="mr-auto">
          <h2 className="font-semibold text-[#1A1F2E]">Unmapped Inward Entries</h2>
          <p className="text-xs text-[#5A5F6E]">Legacy entries with no GPS — select and manually map them to a warehouse</p>
        </div>
        <span className="text-xs bg-amber-100 text-amber-800 px-2.5 py-1 rounded-full font-semibold" data-testid="retag-unmapped-count">
          {count == null ? '…' : count} unmapped
        </span>
      </div>

      {/* Filters */}
      <div className="px-4 py-3 flex items-end gap-3 flex-wrap border-b border-[#E5DFD0]">
        <div>
          <Label className="text-xs">From date</Label>
          <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="h-9 w-40" data-testid="retag-from-date" />
        </div>
        <div>
          <Label className="text-xs">To date</Label>
          <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="h-9 w-40" data-testid="retag-to-date" />
        </div>
        <div className="flex-1 min-w-[180px]">
          <Label className="text-xs">Search</Label>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && loadEntries(0)}
            placeholder="entry / document / staff / vendor / vehicle"
            className="h-9"
            data-testid="retag-search"
          />
        </div>
        <Button variant="outline" className="h-9" onClick={() => loadEntries(0)} data-testid="retag-apply-btn">Apply</Button>
        <Button
          variant="ghost"
          className="h-9 text-[#5A5F6E]"
          onClick={() => { setFromDate(''); setToDate(''); setSearch(''); setTimeout(() => loadEntries(0), 0); }}
          data-testid="retag-clear-btn"
        >
          Clear
        </Button>
      </div>

      {/* Assign bar */}
      <div className="px-4 py-3 flex items-center gap-3 flex-wrap bg-amber-50/40 border-b border-[#E5DFD0]">
        <span className="text-sm text-[#1A1F2E] font-medium" data-testid="retag-selected-count">{selected.size} selected</span>
        <div className="min-w-[220px]">
          <Select value={targetWh} onValueChange={setTargetWh}>
            <SelectTrigger className="h-9" data-testid="retag-warehouse-select">
              <SelectValue placeholder="Assign to warehouse…" />
            </SelectTrigger>
            <SelectContent>
              {activeWarehouses.map((w) => (
                <SelectItem key={w.warehouse_id} value={w.warehouse_id} data-testid={`retag-wh-opt-${w.warehouse_id}`}>
                  {w.name}{w.code ? ` (${w.code})` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          onClick={assign}
          disabled={assigning || selected.size === 0 || !targetWh}
          className="h-9 bg-amber-600 hover:bg-amber-700 text-white"
          data-testid="retag-assign-btn"
        >
          {assigning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          Assign {selected.size || ''} to warehouse
        </Button>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-[#5A5F6E]"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading…</div>
      ) : entries.length === 0 ? (
        <div className="py-12 text-center text-[#5A5F6E]" data-testid="retag-empty">No unmapped entries for this filter.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-[#F5F3EC] text-[#5A5F6E]">
            <tr>
              <th className="px-4 py-2.5 w-10">
                <input type="checkbox" checked={selected.size === entries.length && entries.length > 0} onChange={toggleAll} data-testid="retag-select-all" />
              </th>
              <th className="text-left px-3 py-2.5 font-medium">Entry</th>
              <th className="text-left px-3 py-2.5 font-medium">Document</th>
              <th className="text-left px-3 py-2.5 font-medium">Staff</th>
              <th className="text-left px-3 py-2.5 font-medium">Vendor</th>
              <th className="text-left px-3 py-2.5 font-medium">Date</th>
              <th className="text-right px-3 py-2.5 font-medium">Bundles</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.entry_id} className="border-t border-[#E5DFD0] hover:bg-[#FBFAF5]" data-testid={`retag-row-${e.entry_id}`}>
                <td className="px-4 py-2.5">
                  <input type="checkbox" checked={selected.has(e.entry_id)} onChange={() => toggle(e.entry_id)} data-testid={`retag-check-${e.entry_id}`} />
                </td>
                <td className="px-3 py-2.5 font-mono text-xs text-[#1A1F2E]">
                  {e.entry_id}
                  {e.gps_override ? <span className="ml-2 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">override</span> : null}
                </td>
                <td className="px-3 py-2.5">{e.document_barcode || '—'}</td>
                <td className="px-3 py-2.5">{e.staff_id || '—'}</td>
                <td className="px-3 py-2.5">{e.vendor_name || '—'}</td>
                <td className="px-3 py-2.5 text-[#5A5F6E]">{fmtDate(e.timestamp)}</td>
                <td className="px-3 py-2.5 text-right">{e.bundle_count ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Pagination */}
      {entries.length > 0 && (
        <div className="px-4 py-3 flex items-center justify-between border-t border-[#E5DFD0]">
          <span className="text-xs text-[#5A5F6E]" data-testid="retag-page-info">Page {page + 1} of {totalPages} • {total} total</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 0} onClick={() => loadEntries(page - 1)} data-testid="retag-prev">Prev</Button>
            <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => loadEntries(page + 1)} data-testid="retag-next">Next</Button>
          </div>
        </div>
      )}
    </div>
  );
};

const AdminWarehouses = () => {
  const navigate = useNavigate();
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [locating, setLocating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await whApi.listWarehouses(true);
      setWarehouses(data.warehouses || []);
    } catch (e) {
      toast.error('Failed to load warehouses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (w) => {
    setEditing(w);
    setForm({ name: w.name || '', code: w.code || '', latitude: String(w.latitude ?? ''), longitude: String(w.longitude ?? ''), active: w.active !== false });
    setDialogOpen(true);
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) { toast.error('Geolocation not supported on this device'); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((f) => ({ ...f, latitude: pos.coords.latitude.toFixed(6), longitude: pos.coords.longitude.toFixed(6) }));
        setLocating(false);
        toast.success('Captured current location');
      },
      (err) => { setLocating(false); toast.error(`Location error: ${err.message}`); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const save = async () => {
    const name = form.name.trim();
    const lat = parseFloat(form.latitude);
    const lng = parseFloat(form.longitude);
    if (!name) { toast.error('Warehouse name is required'); return; }
    if (Number.isNaN(lat) || Number.isNaN(lng)) { toast.error('Valid latitude and longitude are required'); return; }
    setSaving(true);
    try {
      const payload = { name, code: form.code.trim(), latitude: lat, longitude: lng, active: form.active };
      if (editing) {
        await whApi.updateWarehouse(editing.warehouse_id, payload);
        toast.success('Warehouse updated');
      } else {
        await whApi.createWarehouse(payload);
        toast.success('Warehouse created');
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save warehouse');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (w) => {
    if (!window.confirm(`Deactivate warehouse "${w.name}"? Existing tagged entries keep their tag.`)) return;
    try {
      await whApi.deleteWarehouse(w.warehouse_id, false);
      toast.success('Warehouse deactivated');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to deactivate');
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F3EC]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" onClick={() => navigate('/admin')} data-testid="wh-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <Warehouse className="w-6 h-6 text-emerald-400" />
          <div>
            <h1 className="text-xl font-semibold">Warehouses</h1>
            <p className="text-xs text-gray-300">Per-site warehouses with GPS coordinates — gate entries auto-tag to the nearest one</p>
          </div>
          <div className="ml-auto">
            <Button onClick={openAdd} className="bg-emerald-600 hover:bg-emerald-700" data-testid="wh-add-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Warehouse
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-[#5A5F6E]"><Loader2 className="w-6 h-6 animate-spin mr-2" />Loading…</div>
        ) : warehouses.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-16 text-center">
              <Warehouse className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-[#1A1F2E] mb-1">No warehouses yet</h3>
              <p className="text-[#5A5F6E] mb-4">Add your site's warehouses and tag their GPS coordinates.</p>
              <Button onClick={openAdd} className="bg-emerald-600 hover:bg-emerald-700" data-testid="wh-add-empty-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Warehouse
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="bg-white rounded-lg border border-[#E5DFD0] overflow-hidden" data-testid="wh-table">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F3EC] text-[#5A5F6E]">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">Name</th>
                  <th className="text-left px-4 py-2.5 font-medium">Code</th>
                  <th className="text-left px-4 py-2.5 font-medium">Coordinates</th>
                  <th className="text-left px-4 py-2.5 font-medium">Status</th>
                  <th className="text-right px-4 py-2.5 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {warehouses.map((w) => (
                  <tr key={w.warehouse_id} className="border-t border-[#E5DFD0]" data-testid={`wh-row-${w.warehouse_id}`}>
                    <td className="px-4 py-2.5 font-medium text-[#1A1F2E]">{w.name}</td>
                    <td className="px-4 py-2.5">{w.code || '—'}</td>
                    <td className="px-4 py-2.5 text-[#5A5F6E]">
                      <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{Number(w.latitude).toFixed(5)}, {Number(w.longitude).toFixed(5)}</span>
                    </td>
                    <td className="px-4 py-2.5">
                      {w.active !== false
                        ? <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Active</span>
                        : <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">Inactive</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(w)} data-testid={`wh-edit-${w.warehouse_id}`}><Pencil className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => remove(w)} data-testid={`wh-delete-${w.warehouse_id}`}><Trash2 className="w-4 h-4" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Admin bulk re-tag tool for legacy unmapped entries */}
        <UnmappedRetagTool warehouses={warehouses} />
      </main>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="wh-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Warehouse' : 'Add Warehouse'}</DialogTitle>
            <DialogDescription>Register the warehouse and its GPS coordinates. Gate entries auto-tag to the nearest active warehouse.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Salem Main Warehouse" data-testid="wh-name-input" />
            </div>
            <div>
              <Label className="text-xs">Code</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="e.g. SLM-A" data-testid="wh-code-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Latitude *</Label>
                <Input value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} placeholder="11.664325" data-testid="wh-lat-input" />
              </div>
              <div>
                <Label className="text-xs">Longitude *</Label>
                <Input value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} placeholder="78.146011" data-testid="wh-lng-input" />
              </div>
            </div>
            <Button type="button" variant="outline" className="w-full" onClick={useCurrentLocation} disabled={locating} data-testid="wh-use-location-btn">
              {locating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LocateFixed className="w-4 h-4 mr-2" />}
              Use my current location
            </Button>
            <div className="flex items-center justify-between pt-1">
              <Label className="text-xs">Active</Label>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="wh-active-switch" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="wh-cancel-btn">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="wh-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editing ? 'Save Changes' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminWarehouses;
